import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import SEO, { breadcrumbSchema } from "@/components/SEO";
import { calculators } from "@/data/calculatorsData";

const toolsPageSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Free Crypto Calculators India",
  description: "Free crypto calculators for Indian investors. Calculate crypto tax (Section 115BBH), TDS (Section 194S), profit and loss, staking rewards, and SIP returns.",
  url: "https://bitgenix.io/tools",
  publisher: {
    "@type": "Organization",
    name: "BitGenix",
    url: "https://bitgenix.io"
  },
  mainEntity: {
    "@type": "ItemList",
    itemListElement: calculators.map((calc, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: calc.title,
      url: `https://bitgenix.io/calculators/${calc.slug}`
    }))
  }
};

export default function Tools() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const toolKeywords = [...new Set(calculators.flatMap((calc) => calc.tags || []))];

  return (
    <div data-testid="tools-page">
      <SEO
        title="Free Crypto Calculators India | Tax, TDS, P&L, Staking, SIP"
        description="Free crypto calculators for Indian investors. Calculate crypto tax under Section 115BBH, TDS under 194S, profit and loss, staking rewards, and SIP returns. Built by BitGenix."
        path="/tools"
        keywords={toolKeywords}
        jsonLd={[
          toolsPageSchema,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Free Tools", path: "/tools" }
          ])
        ]}
      />

      <section className="bg-[#21324D] py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="font-body text-sm uppercase tracking-[0.2em] text-[#A7A55C] mb-6">Free Tools</p>
            <h1 className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#F5F5F2] leading-none tracking-tight mb-6" data-testid="tools-heading">
              Crypto Calculators for India
            </h1>
            <p className="font-body text-lg text-[#F5F5F2]/75 leading-relaxed">
              Free, accurate calculators to help you plan your crypto taxes, track profits, estimate staking yields, and project SIP returns. Built for Indian investors and traders.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-[#F5F5F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {calculators.map((calc) => (
              <Link
                key={calc.slug}
                to={`/calculators/${calc.slug}`}
                className="group bg-white border border-[#383A46]/10 p-8 hover:border-[#A7A55C]/40 transition-all duration-300"
                data-testid={`tool-card-${calc.slug}`}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-[#21324D] flex items-center justify-center">
                    <calc.icon size={20} weight="duotone" className="text-[#A7A55C]" />
                  </div>
                  <h2 className="font-body font-medium text-base text-[#21324D] group-hover:text-[#A7A55C] transition-colors">
                    {calc.shortTitle}
                  </h2>
                </div>
                <p className="font-body text-sm text-[#383A46]/70 leading-relaxed mb-6">
                  {calc.shortDesc}
                </p>
                <div className="flex items-center gap-2 font-body text-sm font-semibold text-[#A7A55C]">
                  Use Calculator <ArrowRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-[#21324D] tracking-tight mb-5">
            Need help beyond calculators?
          </h2>
          <p className="font-body text-sm text-[#383A46]/70 leading-relaxed mb-8 max-w-xl mx-auto">
            These tools give you estimates. For comprehensive crypto tax planning, compliance advisory, or investment consulting, talk to the BitGenix team.
          </p>
          <Link
            to="/contact"
            className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors font-body font-semibold text-sm px-8 py-4 inline-flex items-center gap-2"
            data-testid="tools-cta"
          >
            Book a Free Consultation <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
